import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectAuth } from '../../store/authSlice';
import {
  closeWidget,
  initializeSupportChat,
  selectSupportIsOpen,
  selectSupportUnread,
  toggleWidget,
} from '../../store/supportChatSlice';
import SupportChatLauncher from './SupportChatLauncher';
import SupportChatWindow from './SupportChatWindow';
import './SupportChat.css';

/**
 * Главный компонент чата поддержки.
 * - Содержит FAB (launcher) и панель чата.
 * - Загружает текущий тред при авторизации, чтобы получить unread-бейдж даже без открытия.
 * - Виден только на защищённых страницах (см. ProtectedLayout).
 */
/**
 * Маршруты, на которых FAB и popup-окно поддержки не должны отображаться.
 * На `/support` уже есть полноэкранная версия чата — дублировать её плавающей
 * кнопкой нет смысла.
 */
const HIDDEN_ROUTES = ['/support'];

const SupportChatWidget: React.FC = () => {
  const dispatch = useAppDispatch();
  const auth = useAppSelector(selectAuth);
  const isOpen = useAppSelector(selectSupportIsOpen);
  const unread = useAppSelector(selectSupportUnread);
  const location = useLocation();

  // Bootstrap: кэш → тред → синхронизация истории (unread-бейдж без открытия виджета).
  useEffect(() => {
    if (auth.isAuthenticated && auth.user?.userId) {
      dispatch(initializeSupportChat(auth.user.userId));
    }
  }, [auth.isAuthenticated, auth.user?.userId, dispatch]);

  if (!auth.isAuthenticated) return null;

  const isHiddenRoute = HIDDEN_ROUTES.some(
    (route) => location.pathname === route || location.pathname.startsWith(`${route}/`),
  );
  if (isHiddenRoute) return null;

  return (
    <>
      <div
        className={`sc-window-wrapper ${isOpen ? 'is-open' : 'is-closed'}`}
        aria-hidden={!isOpen}
      >
        {isOpen && <SupportChatWindow onClose={() => dispatch(closeWidget())} />}
      </div>

      <SupportChatLauncher
        isOpen={isOpen}
        unreadCount={unread}
        onClick={() => dispatch(toggleWidget())}
      />
    </>
  );
};

export default SupportChatWidget;
